import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { getTrackerEvents } from "./trackerEventService";
import "./TrackerEventsModal.css";

const PAGE_SIZE = 20;

function formatDate(value) {
    if (!value) {
        return "-";
    }

    const normalizedDate = String(value).replace(" ", "T");
    const date = new Date(normalizedDate);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat("es-PE", {
        dateStyle: "medium",
        timeStyle: "medium",
    }).format(date);
}

function formatEventName(value) {
    if (!value) {
        return "Evento sin nombre";
    }

    return String(value)
        .replaceAll("_", " ")
        .replace(/\b\w/g, (letter) =>
            letter.toUpperCase()
        );
}

function getMetadataEntries(metadata) {
    if (!metadata) {
        return [];
    }

    if (typeof metadata === "object") {
        return Object.entries(metadata);
    }

    try {
        const parsedMetadata = JSON.parse(metadata);

        if (
            parsedMetadata &&
            typeof parsedMetadata === "object"
        ) {
            return Object.entries(parsedMetadata);
        }
    } catch {
        return [["Información", metadata]];
    }

    return [];
}

function renderMetadataValue(value) {
    if (value === null || value === undefined) {
        return "-";
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    if (typeof value === "boolean") {
        return value ? "Sí" : "No";
    }

    return String(value);
}

function formatJson(value) {
    if (value === null || value === undefined || value === "") {
        return "Sin información adicional";
    }

    try {
        const parsedValue =
            typeof value === "string"
                ? JSON.parse(value)
                : value;

        return JSON.stringify(parsedValue, null, 2);
    } catch {
        return String(value);
    }
}

export default function TrackerEventsModal({
    open,
    fecha,
    token,
    userName,
    onClose,
}) {
	console.log("fecha",fecha)
    const [events, setEvents] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [expandedEventId, setExpandedEventId] =
        useState(null);

    const scrollContainerRef = useRef(null);
    const observerRef = useRef(null);
    const loadingRef = useRef(false);
    const pageRef = useRef(1);
    const hasMoreRef = useRef(true);
    const abortControllerRef = useRef(null);

    const loadEvents = useCallback(
        async (pageToLoad, replace = false) => {
            if (
                !token ||
                loadingRef.current ||
                (!replace && !hasMoreRef.current)
            ) {
                return;
            }

            loadingRef.current = true;
            setLoading(true);
            setError("");

            const controller = new AbortController();
            abortControllerRef.current = controller;

            try {
                const response = await getTrackerEvents({
                    token,
                    fecha: fecha,
                    page: pageToLoad,
                    limit: PAGE_SIZE,
                    signal: controller.signal,
                });

                setEvents((currentEvents) => {
                    if (replace) {
                        return response.data;
                    }

                    const existingIds = new Set(
                        currentEvents.map((event) =>
                            String(event.id)
                        )
                    );

                    const newEvents = response.data.filter(
                        (event) =>
                            !existingIds.has(
                                String(event.id)
                            )
                    );

                    return [
                        ...currentEvents,
                        ...newEvents,
                    ];
                });

                pageRef.current = pageToLoad;
                hasMoreRef.current =
                    response.pagination.has_more;

                setPage(pageToLoad);
                setHasMore(response.pagination.has_more);
            } catch (requestError) {
                if (requestError.name !== "AbortError") {
                    setError(
                        requestError.message ??
                            "No se pudieron cargar los eventos"
                    );
                }
            } finally {
                if (!controller.signal.aborted) {
                    loadingRef.current = false;
                    setLoading(false);
                }
            }
        },
        [token]
    );

    useEffect(() => {
        if (!open || !token) {
            return undefined;
        }

        setEvents([]);
        setPage(1);
        setHasMore(true);
        setError("");
        setExpandedEventId(null);

        pageRef.current = 1;
        hasMoreRef.current = true;
        loadingRef.current = false;

        loadEvents(1, true);

        return () => {
            abortControllerRef.current?.abort();
            observerRef.current?.disconnect();
        };
    }, [open, token, loadEvents]);

    const lastEventRef = useCallback(
        (node) => {
            observerRef.current?.disconnect();

            if (
                !node ||
                loadingRef.current ||
                !hasMoreRef.current ||
                !scrollContainerRef.current
            ) {
                return;
            }

            observerRef.current = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];

                    if (
                        entry.isIntersecting &&
                        !loadingRef.current &&
                        hasMoreRef.current
                    ) {
                        loadEvents(
                            pageRef.current + 1
                        );
                    }
                },
                {
                    root: scrollContainerRef.current,
                    rootMargin: "200px",
                    threshold: 0.1,
                }
            );

            observerRef.current.observe(node);
        },
        [loadEvents]
    );

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener(
            "keydown",
            handleKeyDown
        );

        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener(
                "keydown",
                handleKeyDown
            );

            document.body.style.overflow = "";
        };
    }, [open, onClose]);

    if (!open) {
        return null;
    }

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    const toggleEventDetails = (eventId) => {
        setExpandedEventId((currentId) =>
            currentId === eventId
                ? null
                : eventId
        );
    };

    return (
        <div
            className="tracker-events-overlay"
            onMouseDown={handleOverlayClick}
            role="presentation"
        >
            <section
                className="tracker-events-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tracker-events-title"
            >
                <header className="tracker-events-header">
                    <div>
                        <h2 id="tracker-events-title">
                            Eventos realizados
                        </h2>

                        <p>{userName || "Usuario"}</p>
                    </div>

                    <button
                        type="button"
                        className="tracker-events-close"
                        onClick={onClose}
                        aria-label="Cerrar modal"
                    >
                        ×
                    </button>
                </header>

                <div className="tracker-events-token">
                    <span>Token:</span>
                    <strong>{token}</strong>
                </div>

                <div className="tracker-events-columns">
                    <span>Evento</span>
                    <span>Página</span>
                    <span>Fecha</span>
                </div>

                <div
                    ref={scrollContainerRef}
                    className="tracker-events-content"
                >
                    {events.map((trackerEvent, index) => {
                        const isLastEvent =
                            index === events.length - 1;

                        const metadataEntries =
                            getMetadataEntries(
                                trackerEvent.metadata
                            );

                        const isExpanded =
                            expandedEventId ===
                            trackerEvent.id;

                        return (
                            <article
                                ref={
                                    isLastEvent
                                        ? lastEventRef
                                        : null
                                }
                                key={
                                    trackerEvent.id ??
                                    `${trackerEvent.eventName}-${trackerEvent.createdAt}-${index}`
                                }
                                className="tracker-event-item"
                            >
                                <button
                                    type="button"
                                    className="tracker-event-main"
                                    onClick={() =>
                                        toggleEventDetails(
                                            trackerEvent.id
                                        )
                                    }
                                    aria-expanded={
                                        isExpanded
                                    }
                                >
                                    <div className="tracker-event-name">
                                        <span className="tracker-event-icon">
                                            ⚡
                                        </span>

                                        <strong>
                                            {formatEventName(
                                                trackerEvent.name
                                            )}
                                        </strong>
                                    </div>

                                    <span
                                        className="tracker-event-link"
                                        title={
                                            trackerEvent.link
                                        }
                                    >
                                        {trackerEvent.link ||
                                            "Sin página"}
                                    </span>

                                    <time>
                                        {formatDate(
                                            trackerEvent.created_at
                                        )}
                                    </time>

                                    <span className="tracker-event-arrow">
                                        {isExpanded
                                            ? "▲"
                                            : "▼"}
                                    </span>
                                </button>

                                {isExpanded && (
                                    <div className="tracker-event-details">
                                        <h4>
                                            Información del evento
                                        </h4>

                                        {trackerEvent.metadatos.length >
                                        0 ? (
                                            <div className="tracker-event-metadata">

											    <pre className="tracker-event-json">
											        {formatJson(trackerEvent.metadatos)}
											    </pre>

                                            </div>
                                        ) : (
                                            <p className="tracker-event-no-metadata">
                                                Este evento no
                                                contiene información
                                                adicional.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </article>
                        );
                    })}

                    {!loading &&
                        events.length === 0 &&
                        !error && (
                            <div className="tracker-events-empty">
                                No existen eventos registrados
                                para este usuario.
                            </div>
                        )}

                    {error && (
                        <div className="tracker-events-error">
                            <p>{error}</p>

                            <button
                                type="button"
                                onClick={() =>
                                    loadEvents(
                                        events.length === 0
                                            ? 1
                                            : page + 1,
                                        events.length === 0
                                    )
                                }
                            >
                                Reintentar
                            </button>
                        </div>
                    )}

                    {loading && (
                        <div className="tracker-events-loading">
                            <span className="tracker-events-spinner" />
                            Cargando eventos...
                        </div>
                    )}

                    {!loading &&
                        !hasMore &&
                        events.length > 0 && (
                            <div className="tracker-events-end">
                                No hay más eventos.
                            </div>
                        )}
                </div>
            </section>
        </div>
    );
}